import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Backdrop,
  Box,
  CircularProgress,
  Link as MuiLink,
  List,
  ListItem,
  Menu,
  Tooltip,
} from '@mui/material';
import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Style } from '../NotificationsStyle';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import HttpService from '../../hooks/Https-services';
import { API_ROUTES } from '../../utils/constant';
import { GlobalSearchContext } from '../../context/GlobalSearchContext';
import { Link, useLocation } from 'react-router-dom';
import NotificationDialog from '../NotificaionDialog';

// ----------------------------------------------------------------------

export default function ContactsPopover() {
  const [anchorElN, setAnchorElN] = React.useState<null | HTMLElement>(null);
  const openN = Boolean(anchorElN);
  const [loading, setLoading] = useState(false);
  const [currentElement, setCurrentElement] = useState('Unread');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasFetched, setHasFetched] = React.useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<any>(null);
  const { count, fetchCount, fetchBroadcastNotifications }: any =
    useContext(GlobalSearchContext);

  // Pagination states
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    limit: 20,
  });
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationRef = useRef<HTMLDivElement | null>(null);
  const menuContentRef = useRef<HTMLDivElement | null>(null);

  // Reset pagination when tab changes
  useEffect(() => {
    setNotifications([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setHasMore(true);
  }, [currentElement]);

  const fetchList = useCallback(
    async (pageNum = 1, isLoadMore = false) => {
      try {
        if (isLoadMore) {
          setIsLoadingMore(true);
        } else {
          setLoading(true);
        }

        const query: any = {
          page: pageNum,
          limit: pagination.limit,
        };

        if (currentElement == 'Unread' || currentElement == 'Read') {
          query.type = currentElement == 'Unread' ? 0 : 1;
        } else if (currentElement == 'Broadcast') {
          query.type = 7;
        }

        const response = await HttpService.get(
          `${API_ROUTES.notificationList}?${new URLSearchParams(query).toString()}`
        );
        const listData = response?.data?.data?.list || [];
        const { currentPage, totalCount, totalPages } =
          response?.data?.data || {};

        if (isLoadMore) {
          setNotifications((prev) => [...prev, ...listData]);
        } else {
          setNotifications(listData);
        }

        // Update pagination info
        if (currentPage && totalCount && totalPages) {
          setPagination({
            currentPage,
            totalCount,
            totalPages,
            limit: pagination.limit,
          });

          // Check if there are more notifications to load
          setHasMore(currentPage < totalPages);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [currentElement, pagination.limit]
  );

  // Set up Intersection Observer for lazy loading
  // Replace the Intersection Observer useEffect with this optimized version
  useEffect(() => {
    if (isLoadingMore || !hasMore || !openN) return;

    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && notifications.length > 0) {
          fetchList(pagination.currentPage + 1, true);
        }
      },
      { threshold: 0.1 } // Reduced threshold to prevent early triggering
    );

    if (lastNotificationRef.current) {
      observer.current.observe(lastNotificationRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [
    notifications,
    isLoadingMore,
    hasMore,
    pagination.currentPage,
    fetchList,
    openN,
  ]);

  const handleBellClick = React.useCallback(() => {
    if (!hasFetched) {
      setHasFetched(true);
      // Don't call fetchList here, let the useEffect handle it
    }
  }, [hasFetched]);

  useEffect(() => {
    if (hasFetched && openN) {
      fetchList(1, false);
    }
  }, [hasFetched, openN, currentElement]);

  const markAsRead = async (id: any) => {
    try {
      setLoading(true);
      if (currentElement == 'Unread' || currentElement == 'Read') {
        const data = { type: currentElement == 'Unread' ? 1 : 0 };
        await HttpService.patch(`${API_ROUTES.markAs}/${id}`, data);
      } else if (currentElement == 'Broadcast') {
        const data = { type: 0 };
        await HttpService.patch(`${API_ROUTES.markAs}/${id}`, data);
        fetchBroadcastNotifications();
      }
      fetchList(1, false);
      fetchCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsAllRead = async () => {
    try {
      setLoading(true);
      const data = { type: 1 };
      await HttpService.patch(`${API_ROUTES.markAsAll}`, data);
      fetchList(1, false);
      fetchCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickN = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElN(event.currentTarget);
    handleBellClick();
  };

  const handleCloseN = () => {
    setAnchorElN(null);
    setHasFetched(false);
  };

  const handleDialogOpen = (alert: any) => {
    setCurrentAlert(alert);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setCurrentAlert(null);
  };

  return (
    <>
      <IconButton
        onClick={handleClickN}
        aria-controls={openN ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openN ? 'true' : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: '4px',
          mr: 2,
        }}
        id="iconbtn_index_9"
      >
        <Badge color="error" badgeContent={count} max={9}>
          <NotificationsIcon sx={{ color: 'white' }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorElN}
        open={openN}
        onClose={handleCloseN}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            width: 370,
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        id="menu_index_12"
      >
        <MenuItem
          disableTouchRipple
          sx={{ justifyContent: 'space-between' }}
          id="menuitem_index_13"
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 600 }}
            id="lbl_index_14"
          >
            Notifications
          </Typography>
          <IconButton onClick={handleCloseN} id="iconbtn_index_15">
            <CloseIcon sx={{ color: '#9D9D9D' }} />
          </IconButton>
        </MenuItem>
        <List
          sx={{
            ...Style.CompanySetting.MenuList,
            px: 2,
            justifyContent: 'space-between',
          }}
          id="list_index_16"
        >
          <ListItem
            disablePadding
            sx={{ width: 'auto' }}
            id="listitem_index_17"
          >
            <MuiLink
              onClick={() => setCurrentElement('Unread')}
              style={{ cursor: 'pointer', fontSize: '14px' }}
              className={currentElement === 'Unread' ? 'active' : ''}
              sx={Style.CompanySetting.MenuListItem}
              id="lnk_index_18"
            >
              Unread
            </MuiLink>
            <MuiLink
              onClick={() => setCurrentElement('Read')}
              style={{ cursor: 'pointer', fontSize: '14px' }}
              className={currentElement === 'Read' ? 'active' : ''}
              sx={Style.CompanySetting.MenuListItem}
              id="lnk_index_19"
            >
              Read
            </MuiLink>
            <MuiLink
              onClick={() => setCurrentElement('Broadcast')}
              style={{ cursor: 'pointer', fontSize: '14px' }}
              className={currentElement === 'Broadcast' ? 'active' : ''}
              sx={Style.CompanySetting.MenuListItem}
              id="lnk_index_20"
            >
              Broadcast
            </MuiLink>
          </ListItem>
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={markAsAllRead}
            id="box_index_20"
          >
            {currentElement !== 'Broadcast' && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: '#436AF3' }}
                id="lbl_index_21"
              >
                Mark All as Read
              </Typography>
            )}
          </Box>
        </List>
        <Box
          ref={menuContentRef}
          sx={{
            px: 2,
            maxHeight: '400px',
            overflowY: 'auto',
          }}
          id="box_index_22"
        >
          {notifications.length > 0
            ? notifications.map((el: any, index) => (
                <Box
                  key={el.id}
                  ref={
                    index === notifications.length - 1
                      ? lastNotificationRef
                      : null
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                  id="box_index_23"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    id="box_index_24"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      id="box_index_25"
                    >
                      <Box id="box_index_26">
                        {el.created_by ? (
                          <Box sx={Style.UserNameRound} id="box_index_27">
                            <img
                              width={'100%'}
                              height="100%"
                              style={{
                                objectFit: 'cover',
                                borderRadius: '50%',
                              }}
                              src={`${el.created_by.ico}`}
                              alt=""
                            />
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={Style.UserNameRound}
                            id="lbl_index_28"
                          >
                            <Avatar></Avatar>
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box id="box_index_29">
                      <Typography variant="body2" id="lbl_index_30">
                        {currentElement === 'Broadcast' ? (
                          <Box
                            onClick={() => handleDialogOpen(el)}
                            style={{
                              textDecoration: 'none',
                              textTransform: 'none',
                              color: 'black',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'bold',
                                display: 'block',
                              }}
                              id="span_title_33"
                            >
                              {el.message}
                            </span>
                          </Box>
                        ) : (
                          <Link
                            to={el.path}
                            onClick={handleCloseN}
                            style={{
                              textDecoration: 'none',
                              textTransform: 'none',
                              color: 'black',
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'bold',
                                display: 'block',
                              }}
                              id="span_title_33"
                            >
                              {el.message}
                            </span>
                          </Link>
                        )}

                        <span
                          style={{
                            wordBreak: 'break-word',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: el.body_html,
                          }}
                          id="span_index_31"
                        ></span>
                        <span
                          style={{
                            color: 'rgba(183, 183, 183, 1)',
                          }}
                          id="span_index_32"
                        >
                          {moment(el.createdAt).fromNow()}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                  <Box id="box_index_33">
                    <Box
                      sx={{
                        cursor: 'pointer',
                        height: '14px',
                        p: 0.25,
                        width: '14px',
                        border: '1px solid #DADADA',
                        borderRadius: '50%',
                      }}
                      id="box_index_34"
                    >
                      <Tooltip
                        title={
                          currentElement === 'Broadcast'
                            ? 'Mark as Unread'
                            : `Mark as ${el.status === 'read' ? 'Unread' : 'Read'}`
                        }
                        id="ttp_index_35"
                      >
                        <Box
                          onClick={() => markAsRead(el.id)}
                          sx={{
                            height: 1,
                            width: 1,
                            borderRadius: '50%',
                            backgroundColor:
                              el.status != 'read' ? '#436AF3' : 'transparent',
                          }}
                          id="box_index_36"
                        ></Box>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              ))
            : !loading && (
                <Typography
                  variant="body1"
                  sx={{ textAlign: 'center', fontWeight: 600, py: 2 }}
                >
                  No notifications found
                </Typography>
              )}

          {/* Loading indicator for lazy loading */}
          {isLoadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* No more notifications message */}
          {!hasMore && notifications.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                py: 2,
              }}
            >
              No more notifications
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: 'center' }} id="box_index_37">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              color: '#B7B7B7',
            }}
            id="lbl_index_38"
          >
            Notifications from the last 30 days.
          </Typography>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading && !isLoadingMore}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Menu>
      <NotificationDialog
        loading={loading}
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        currentAlert={currentAlert}
        isDispalyMarkAs={false}
      />
    </>
  );
}
